import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Roles allowed to view the employee's Annual salary field.
const SALARY_VIEW_ROLES = ["super_admin", "hr", "manager"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeSalary = SALARY_VIEW_ROLES.includes(user?.role);
  const [emp, setEmp] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [assets, setAssets] = useState([]);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    api.get(`/employees/${id}`).then((r) => setEmp(r.data));
    api.get("/leaves").then((r) => setLeaves(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
    api.get("/goals").then((r) => setGoals(r.data));
  }, [id]);

  if (!emp) return <div className="text-sm text-gray-500">Loading…</div>;

  const myLeaves = leaves.filter((l) => l.employee_id === emp.employee_id);
  const myAssets = assets.filter((a) => a.assigned_to === emp.employee_id);
  const myGoals = goals.filter((g) => g.employee_id === emp.employee_id);

  return (
    <div data-testid="employee-detail-page">
      <Button data-testid="back-to-employees" variant="ghost" className="mb-2 -ml-2" onClick={() => navigate("/employees")}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      <Card className="hr-card p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-full bg-[#1E88E5] text-white grid place-items-center font-display text-xl font-bold">
            {(emp.name || "U").split(" ").map((s) => s[0]).slice(0,2).join("")}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-semibold text-[#0F172A]">{emp.name}</h1>
              <StatusBadge status={emp.status} />
              {emp.onboarding_synced_at && (
                <span
                  data-testid="onboarding-sync-tag"
                  className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                  title={`Synced from ${emp.onboarding_synced_from} on ${emp.onboarding_synced_at}`}
                >
                  Onboarding · {emp.onboarding_synced_from === "hr_edit" ? "HR-edited" : "synced"}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-500 mt-1">{emp.designation} · {emp.department} · {emp.location}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <Info label="Employee ID" value={emp.employee_id} mono />
              <Info label="Email" value={emp.email} />
              <Info
                label="Mobile Number"
                value={emp.mobile || emp.phone}
                mono
                data-testid="emp-mobile-number"
              />
              <Info
                label="Manager Name"
                value={emp.manager || emp.reporting_manager_name}
                data-testid="emp-manager-name"
              />
              <Info label="Joining date" value={emp.joining_date} />
              <Info label="DOB" value={emp.dob} />
              <Info label="Blood group" value={emp.blood_group} />
              <Info label="PAN" value={emp.pan} mono />
            </div>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="personal">
        <TabsList className="bg-transparent border-b border-gray-200 rounded-none w-full justify-start h-auto p-0 gap-6">
          {["personal", "id_proof", "bank", "emergency", "education", "experience", "leaves", "assets", "performance", "documents"].map((t) => (
            <TabsTrigger
              key={t}
              value={t}
              data-testid={`emp-tab-${t}`}
              className="capitalize border-b-2 border-transparent data-[state=active]:border-[#F26B3A] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-none rounded-none px-1 pb-3 bg-transparent"
            >
              {t === "id_proof" ? "ID proofs" : t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="personal" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-personal-card">
            <Info label="Date of birth" value={emp.dob} />
            <Info label="Gender" value={emp.gender} />
            <Info label="Marital status" value={emp.marital_status} />
            <Info label="Blood group" value={emp.blood_group} />
            <Info label="Father's name" value={emp.father_name} />
            <Info label="Mother's name" value={emp.mother_name} />
            <Info label="Current address" value={emp.address} />
            <Info label="Permanent address" value={emp.permanent_address} />
            {canSeeSalary && (
              <Info
                label="Annual salary"
                value={emp.salary ? `₹ ${emp.salary.toLocaleString("en-IN")}` : "—"}
                data-testid="emp-annual-salary"
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="id_proof" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-idproof-card">
            <Info label="Aadhaar" value={emp.aadhaar} mono />
            <Info label="PAN" value={emp.pan} mono />
            <Info label="Passport" value={emp.passport} mono />
            <Info label="Voter ID" value={emp.voter_id} mono />
          </Card>
        </TabsContent>

        <TabsContent value="bank" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-bank-card">
            <Info label="Account holder" value={emp.bank_account_holder} />
            <Info label="Account number" value={emp.bank_account_number} mono />
            <Info label="IFSC" value={emp.bank_ifsc} mono />
            <Info label="Bank" value={emp.bank_name} />
            <Info label="Branch" value={emp.bank_branch} />
            <Info label="UAN" value={emp.uan} mono />
            <Info label="PF number" value={emp.pf_number} mono />
          </Card>
        </TabsContent>

        <TabsContent value="emergency" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-emergency-card">
            <Info label="Contact name" value={emp.emergency_contact_name} />
            <Info label="Relation" value={emp.emergency_contact_relation} />
            <Info label="Phone" value={emp.emergency_contact_phone} mono />
            <Info label="Alt phone" value={emp.onboarding_details?.emergency?.alt_phone} mono />
            <Info label="Address" value={emp.onboarding_details?.emergency?.address} />
          </Card>
        </TabsContent>

        <TabsContent value="education" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-education-card">
            <Info label="Highest qualification" value={emp.highest_qualification} />
            <Info label="University" value={emp.university} />
            <Info label="Year of passing" value={emp.year_of_passing} />
            <Info label="Percentage / CGPA" value={emp.onboarding_details?.education?.percentage} />
          </Card>
        </TabsContent>

        <TabsContent value="experience" className="mt-6">
          <Card className="hr-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="emp-experience-card">
            <Info label="Total experience (yrs)" value={emp.experience_years} />
            <Info label="Previous company" value={emp.previous_company} />
            <Info label="Previous designation" value={emp.previous_designation} />
            <Info label="Previous CTC" value={emp.onboarding_details?.experience?.previous_ctc} />
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-6">
          <Card className="hr-card p-6">
            {myLeaves.length === 0 ? <div className="text-sm text-gray-500">No leave history.</div> : (
              <ul className="divide-y divide-gray-100">
                {myLeaves.map((l) => (
                  <li key={l.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{l.type} · {l.days} day(s)</div>
                      <div className="text-xs text-gray-500">{l.from_date} → {l.to_date} · {l.reason}</div>
                    </div>
                    <StatusBadge status={l.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="assets" className="mt-6">
          <Card className="hr-card p-6">
            {myAssets.length === 0 ? <div className="text-sm text-gray-500">No assets assigned.</div> : (
              <ul className="divide-y divide-gray-100">
                {myAssets.map((a) => (
                  <li key={a.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.type} · {a.model}</div>
                      <div className="text-xs text-gray-500 font-mono">SN: {a.serial} · since {a.assigned_on}</div>
                    </div>
                    <StatusBadge status={a.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <Card className="hr-card p-6">
            {myGoals.length === 0 ? <div className="text-sm text-gray-500">No goals set yet.</div> : (
              <ul className="divide-y divide-gray-100">
                {myGoals.map((g) => (
                  <li key={g.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{g.title}</div>
                      <StatusBadge status={g.status} />
                    </div>
                    <div className="text-xs text-gray-500">{g.kra} · {g.kpi} · {g.review_cycle}</div>
                    <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#1E88E5]" style={{ width: `${g.progress}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <Card className="hr-card p-6 text-sm text-gray-500">
            Document vault coming next. Aadhaar, PAN, offer letter, payslips will appear here once uploaded.
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className={`text-sm text-gray-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
